import React, { useEffect, useMemo, useState } from "react";
import { getInitialAvatarJSX } from "../../../../../utils/stringUtils";
import { useDispatch, useSelector } from "react-redux";
import "./UserSpark.css";
import { getSparkStatus } from "../../../../../utils/SparkUtils";
import { setOpenModal } from "../../../store/controlPanelSlice";
import { useTranslation } from "react-i18next";
import DormantIcon from '../../../../../assets/spark-icons/DormantIcon.svg';
import NaissantIcon from '../../../../../assets/spark-icons/NaissantIcon.svg';
import RayonnantIcon from '../../../../../assets/spark-icons/RayonnantIcon.svg';
import SolaireIcon from '../../../../../assets/spark-icons/SolaireIcon.svg';
import { getSparksData } from "../../../store/Spark/SparkThunks";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';

export default function UserSpark({ onClose }) {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { localAvatar } = useSelector((state) => state.auth);

    const {
        DailySparks,
        MySparks
    } = useSelector((state) => state.Spark);

    const [sparksData, setSparksData] = useState(null);
    const [daysRemaining, setDaysRemaining] = useState(0);

    const { label, icon } = getSparkStatus(MySparks);

    const statusLevels = ['Dormant', 'Naissant', 'Rayonnant', 'Solaire'];
    const currentLevelIndex = statusLevels.indexOf(label);
    const getStepOpacity = (levelName) => {
        return statusLevels.indexOf(levelName) <= currentLevelIndex ? 1 : 0.4;
    };
    useEffect(() => {
        const getSparks = async () => {
            const response = await dispatch(getSparksData()).unwrap();
            setSparksData(response);
            const daysLeft = getDaysRemaining(response.cycleEndDate);
            setDaysRemaining(daysLeft);
        }
        getSparks();
    }, [])

    const last12DaysWithSparks = useMemo(() => {
        if (!sparksData || !Array.isArray(sparksData.sparksReceivedPerDay)) {
            return [];
        }

        const sparkMap = sparksData.sparksReceivedPerDay.reduce((acc, item) => {
            acc[item.day] = item.total;
            return acc;
        }, {});

        const today = new Date();
        const result = [];

        for (let i = 11; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);

            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const dayStr = `${yyyy}-${mm}-${dd}`;

            result.push({
                day: dayStr,
                total: sparkMap[dayStr] || 0
            });
        }

        return result;
    }, [sparksData]);

    const getDaysRemaining = (endDateISO) => {
        if (!endDateISO) return 0;

        const end = new Date(endDateISO);
        const now = new Date();

        end.setUTCHours(0, 0, 0, 0);
        now.setUTCHours(0, 0, 0, 0);

        const diffMs = end - now;
        const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        return days > 0 ? days : 0;
    };
    const cancel = () => {
        onClose();
    };

    const openLeaderboard = () => {
        dispatch(setOpenModal("Spark_leaderboard"));
    };

    const OpenSendSparkModal = () => {
        onClose();
        dispatch(setOpenModal("SendSparks"));
    };
    const formatDateRange = (data) => {
        if (!data?.length) return '';
        const start = new Date(data[0].day);
        const end = new Date(data[data.length - 1].day);

        const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
        const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

        const sDay = days[start.getDay()];
        const sDate = String(start.getDate()).padStart(2, '0');
        const eDay = days[end.getDay()];
        const eDate = String(end.getDate()).padStart(2, '0');
        const month = months[start.getMonth()];
        const year = start.getFullYear();

        return `${sDay} ${sDate} - ${eDay} ${eDate} ${month} ${year}`;
    };

    const chartData = useMemo(() => {
        const data = last12DaysWithSparks.map(item => {
            const d = new Date(item.day);
            const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
            const dayName = days[d.getDay()];

            return {
                name: item.day,
                value: item.total,
                dayName: dayName,
                amount: item.total,
                shortDate: item.day
            };
        });

        return data;
    }, [last12DaysWithSparks]);



    const domainMax = useMemo(() => {
        if (!chartData || chartData.length === 0) return 10;

        const maxVal = Math.max(...chartData.map(d => d.value), 1);
        const availableHeight = 60;
        const desiredMaxPx = 40;

        const safeMax = Math.max(maxVal, 3);

        const calculated = Math.ceil((safeMax * availableHeight) / desiredMaxPx);

        const final = Math.max(calculated, maxVal + 2);

        return final;
    }, [chartData]);



    const dateRangeText = useMemo(() => formatDateRange(last12DaysWithSparks), [last12DaysWithSparks]);
    return (
        <>
            <div className="Spark-Modal" onClick={cancel}>
                <div className="personal-Spark-modal-centered" onClick={(e) => e.stopPropagation()}>
                    <div className="overflow-spark-container">
                        <div className="personal-spark-user">
                            <div className="personal-info">
                                <span className="bg-avatar"
                                    style={{
                                        width: "2.8125rem",
                                        height: "2.8125rem",
                                        padding: "0.125rem",
                                        borderRadius: "0.625rem",
                                        overflow: 'unset',
                                        position: 'relative',
                                        display: 'inline-block',
                                        flexShrink: 0,
                                        backgroundColor: '#22C55E',
                                    }} >
                                    {localAvatar?.urlphoto === "Avatar.png" ?
                                        getInitialAvatarJSX(
                                            localAvatar?.firstName,
                                            localAvatar?.lastName,
                                        )
                                        :
                                        <img
                                            alt="User Avatar"
                                            src={localAvatar?.urlphoto}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                BorderBottom: "0.5rem",
                                                borderRadius: "0.5rem",

                                            }}
                                        />
                                    }
                                </span>
                                <div className="User-info">
                                    <span className="user-info-name">
                                        {localAvatar?.firstName}{localAvatar?.lastName}
                                    </span>
                                    <span className="user-info-connected">
                                        {t("connected")}
                                    </span>
                                    <span className="user-spark-status">
                                        <img
                                            src={icon}
                                            alt={label}
                                            className="user-spark-icon"

                                        />
                                        <span className="user-spark-label">{t(label)}</span>
                                    </span>

                                </div>
                            </div>
                            <div className="personal-info-Spark">
                                <div className="user-sparks">
                                    <span className="user-sparks-number">
                                        {DailySparks}
                                    </span>
                                    <span className="user-sparks-message">
                                        {t("sparks_hand_today")}
                                    </span>
                                </div>
                                 {DailySparks > 0 && (
                                <div className="send-sparks-button">
                                    <button type="button" className="Spark-modal-button-send-sparks" style={{ width: "100%" }} onClick={OpenSendSparkModal}>
                                        {t("distibute_now")}
                                    </button>
                                </div>
                                )}
                            </div>
                        </div>
                        <div className="user-spark-details">
                            <div className="user-spark-data">
                                <div className="user-sparks-container-case">
                                    <span className="user-sparks-container-case-number">
                                        {sparksData?.totalSparksReceived}
                                    </span>
                                    <span className="user-sparks-container-case-title">
                                        {t("sparks_arrive")}
                                    </span>
                                </div>
                                <div className="user-sparks-container-case">
                                    <span className="user-sparks-container-case-number">
                                        {sparksData?.totalSparksSent}
                                    </span>
                                    <span className="user-sparks-container-case-title">
                                        {t("sparks_dist")}
                                    </span>
                                </div>
                                <div className="user-sparks-container-case">
                                    <span className="user-sparks-container-case-number">
                                        {daysRemaining} j
                                    </span>
                                    <span className="user-sparks-container-case-title">
                                        {t("next_reset")}
                                    </span>
                                </div>
                            </div>
                            <div className="spark-info">
                                <span className="spark-info-message">
                                    {t("satellite_status")}
                                </span>
                                <div className="Spark-steps-details">
                                    <div className="Spark-steps-icons">
                                        <div className="Spark-step-icon-div" style={{ opacity: getStepOpacity('Dormant') }}>
                                            <img
                                                src={DormantIcon}
                                                alt="Spark Dormant"
                                                className="Spark-step-icon"
                                            />
                                        </div>
                                        <div className="Spark-step-icon-div" style={{ opacity: getStepOpacity('Naissant') }}>
                                            <img
                                                src={NaissantIcon}
                                                alt="Spark Growing"
                                                className="Spark-step-icon"
                                            />
                                        </div>
                                        <div className="Spark-step-icon-div" style={{ opacity: getStepOpacity('Rayonnant') }}>
                                            <img
                                                src={RayonnantIcon}
                                                alt="Spark Shining"
                                                className="Spark-step-icon"
                                            />
                                        </div>
                                        <div className="Spark-step-icon-div" style={{ opacity: getStepOpacity('Solaire') }}>
                                            <img
                                                src={SolaireIcon}
                                                alt="Spark Sunny"
                                                className="Spark-step-icon"
                                            />
                                        </div>
                                    </div>
                                    <div className="Spark-steps-icons">
                                        <div className="Spark-step1-data" style={{ opacity: getStepOpacity('Dormant') }}>
                                            <span className="Spark-step1-data-title">
                                                {t("DORMANT")}
                                            </span>
                                            <span className="Spark-step1-data-number">
                                                0-5 sat
                                            </span>
                                        </div>
                                        <div className="Spark-step1-data" style={{ opacity: getStepOpacity('Naissant') }} >
                                            <span className="Spark-step1-data-title">
                                                {t("Naissant")}
                                            </span>
                                            <span className="Spark-step1-data-number">
                                                6-20 sat
                                            </span>
                                        </div>
                                        <div className="Spark-step1-data" style={{ opacity: getStepOpacity('Rayonnant') }}>
                                            <span className="Spark-step1-data-title">
                                                {t("Rayonnant")}
                                            </span>
                                            <span className="Spark-step1-data-number">
                                                21-60 sat
                                            </span>
                                        </div>
                                        <div className="Spark-step1-data" style={{ opacity: getStepOpacity('Solaire') }}>
                                            <span className="Spark-step1-data-title">
                                                {t("Solaire")}
                                            </span>
                                            <span className="Spark-step1-data-number">
                                                61 + sat
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="spark-stats">
                                <div className="user-spark-stats">
                                    <span className="user-week-spark-stats-title"> {t("spark_receive")} </span>
                                    <button type="button" className="Spark-modal-button-cancel" onClick={openLeaderboard}>
                                        {t("view_leaderboard")}
                                    </button>
                                </div>
                                <div className="user-spark-stats-week-dates">
                                    <span className="user-week-spark-dates" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                                        {dateRangeText}
                                    </span>

                                    <div className="spark-chart-scoped" style={{ width: '100%', height: 180, marginTop: '8px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={chartData}
                                                tabIndex={-1}
                                            >
                                                <XAxis
                                                    dataKey="dayName"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    height={60}
                                                    interval={0}
                                                    tickMargin={5}
                                                    tick={(props) => {
                                                        const { x, y, index } = props;
                                                        const data = chartData[index];

                                                        if (!data) return null;

                                                        const formatDate = (str) => {
                                                            if (!str) return '';
                                                            const d = new Date(str);
                                                            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                                                        };

                                                        return (
                                                            <g transform={`translate(${x},${y})`}>
                                                                <text
                                                                    x={0}
                                                                    y={-100}
                                                                    textAnchor="middle"
                                                                    fill="#374151"
                                                                    fontSize={11}
                                                                    fontWeight={600}
                                                                >
                                                                    {data.dayName}
                                                                </text>

                                                                <text
                                                                    x={0}
                                                                    y={-90}
                                                                    textAnchor="middle"
                                                                    fill="#374151"
                                                                    fontSize={11}
                                                                    fontWeight={600}
                                                                >
                                                                    {data.amount}
                                                                </text>
                                                                <text
                                                                    x={0}
                                                                    y={18}
                                                                    textAnchor="middle"
                                                                    fill="#9CA3AF"
                                                                    fontSize={10}
                                                                >
                                                                    {formatDate(data.name)}
                                                                </text>
                                                            </g>
                                                        );
                                                    }}
                                                />

                                                <YAxis hide domain={[0, domainMax]} />



                                                <Bar
                                                    dataKey="value"
                                                    fill="#22C55E"
                                                    radius={[6, 6, 0, 0]}
                                                    barSize={18}
                                                    animationDuration={0}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}